import apiClient from "../../app/apiClient";

export const DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/gif"];
export function validateDocument(title, files, retained = []) {
  if (!title.trim() || title.trim().length > 200) return "Enter a title between 1 and 200 characters.";
  if (!files.length && !retained.length) return "Add at least one file.";
  if (files.length + retained.length > 10) return "A document can contain up to 10 files.";
  if (files.some((file) => !DOCUMENT_TYPES.includes(file.type))) return "Choose PDF, JPG, PNG, WebP or GIF files.";
  if (files.some((file) => !file.size || file.size > 20 * 1024 * 1024)) return "Each file must be non-empty and no larger than 20 MB.";
  return "";
}
const path = (projectId, admin) => projectId == null ? "/client/documents" : `/${admin ? "admin" : "client"}/project/${encodeURIComponent(projectId)}/documents`;
export const documentsApi = {
  async list(projectId, admin, page = 0, search = "") {
    const { data } = await apiClient.get(path(projectId, admin), { params: { page, size: 10, search, sort: "createdAt,desc" } });
    if (!Array.isArray(data?.content) || !Number.isInteger(data.totalPages) || !Number.isInteger(data.totalElements)) throw new Error("The server returned an invalid document list.");
    if (data.content.some((document) => document.id == null || document.projectId == null || typeof document.title !== "string" || !Array.isArray(document.files) || document.files.some((file) => file.id == null || typeof file.name !== "string" || !Number.isFinite(file.size)))) throw new Error("The server returned invalid document details.");
    return data;
  },
  async save(projectId, documentId, title, files, retained, onUploadProgress) {
    const body = new FormData();
    body.append("title", title.trim());
    if (documentId != null) body.append("retainedFileIds", JSON.stringify(retained.map((file) => file.id)));
    files.forEach((file) => body.append("files", file));
    const url = path(projectId, true) + (documentId == null ? "" : "/" + encodeURIComponent(documentId));
    return apiClient.request({ url, method: documentId == null ? "post" : "put", data: body, headers: { "Content-Type": undefined }, onUploadProgress });
  },
  remove: (projectId, documentId) => apiClient.delete(path(projectId, true) + "/" + encodeURIComponent(documentId)),
  async file(projectId, documentId, fileId, admin) {
    try {
      const { data } = await apiClient.get(path(projectId, admin) + "/" + encodeURIComponent(documentId) + "/files/" + encodeURIComponent(fileId), { responseType: "blob" });
      if (!DOCUMENT_TYPES.includes(data.type.split(";")[0])) throw new Error("This file format cannot be previewed.");
      return data;
    } catch (error) {
      // Axios also returns error bodies as blobs when requesting file bytes.
      const body = error.response?.data;
      if (body instanceof Blob) {
        try {
          const text = typeof body.text === "function" ? await body.text() : await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
            reader.readAsText(body);
          });
          try { error.response.data = JSON.parse(text); }
          catch { error.response.data = body.type.startsWith("text/plain") ? { message: text } : {}; }
        } catch { /* Keep the original HTTP error if the body cannot be read. */ }
      }
      if (error.response?.status === 400) {
        const details = error.response.data;
        const reason = details?.message || details?.detail || (typeof details === "string" ? details : "");
        throw new Error(typeof reason === "string" && reason.trim()
          ? "Unable to open document: " + reason
          : "The server rejected the file preview request (HTTP 400). Please ask the project team to check the document file endpoint.");
      }
      throw error;
    }
  },
};
